<template>
  <div class="public-legal-page">
    <header class="public-legal-header">
      <a href="https://www.askmaix.com" aria-label="Visit the Maix website">
        <img :src="maixWordmark" alt="Maix">
      </a>

      <label class="legal-page-selector">
        <span class="visually-hidden">Choose a legal document</span>
        <select :value="currentPath" @change="openLegalPage">
          <option
            v-for="legalPage in legalPages"
            :key="legalPage.path"
            :value="legalPage.path"
          >
            {{ legalPage.label }}
          </option>
        </select>
      </label>
    </header>

    <main class="public-legal-main">
      <div v-if="loading" class="public-legal-status" role="status">
        <span class="public-legal-spinner" aria-hidden="true"></span>
        <p>Loading {{ pageTitle }}…</p>
      </div>

      <section v-else-if="errorMessage" class="public-legal-status public-legal-error" role="alert">
        <h1>{{ pageTitle }}</h1>
        <p>{{ errorMessage }}</p>
        <button type="button" @click="loadDocument">Try Again</button>
      </section>

      <article v-else class="public-legal-document">
        <template v-for="(block, index) in formattedBlocks" :key="`${block.type}-${index}`">
          <h1 v-if="block.type === 'heading-1'">
            <InlineMarkdown :text="block.text" />
          </h1>
          <h2 v-else-if="block.type === 'heading-2'">
            <InlineMarkdown :text="block.text" />
          </h2>
          <h3 v-else-if="block.type === 'heading-3'">
            <InlineMarkdown :text="block.text" />
          </h3>

          <ul v-else-if="block.type === 'list'">
            <li v-for="(item, itemIndex) in block.items" :key="itemIndex">
              <InlineMarkdown :text="item" />
            </li>
          </ul>

          <ol v-else-if="block.type === 'ordered-list'">
            <li v-for="(item, itemIndex) in block.items" :key="itemIndex">
              <InlineMarkdown :text="item" />
            </li>
          </ol>

          <p v-else><InlineMarkdown :text="block.text" /></p>
        </template>
      </article>
    </main>

    <footer class="public-legal-footer">
      <div class="public-legal-footer-content">
        <p class="footer-eyebrow">Ready to get started?</p>
        <h2>Download Maix and get smart, simple, legal help.</h2>

        <a
          class="app-store-button"
          href="https://apps.apple.com/us/app/maix-legal/id6743629297"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Download Maix Legal on the App Store"
        >
          <i class="fab fa-apple apple-mark" aria-hidden="true"></i>
          <span>
            <small>Available on the</small>
            <strong>App Store</strong>
          </span>
        </a>

        <a
          class="built-by-button"
          href="https://www.thelomaxlawfirm.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          Built by The Lomax Law Firm
        </a>

        <div class="footer-separator" aria-hidden="true"></div>

        <section class="maix-chat-notice" aria-labelledby="maix-chat-notice-title">
          <h3 id="maix-chat-notice-title">Important Information About Maix Chat</h3>

          <p>
            Maix Chat is a legal-preparation service operated by Lomax Law Firm. It is designed to help App Clients describe a potential legal issue, organize relevant facts, and prepare an attorney request for review by a licensed attorney. Maix Chat is not intended for unrelated or general-purpose AI inquiries.
          </p>
          <p>
            Maix is an artificial-intelligence assistant, not a licensed attorney. Its responses help prepare and organize information for potential attorney review, but they are not personalized legal advice and have not been reviewed by an attorney unless the App expressly tells you otherwise.
          </p>
          <p>
            Maix Chat conversations are stored using the App’s encryption safeguards in a confidential legal-preparation file associated with the user’s account. When the user is ready, relevant information from those conversations may be used to create and submit an attorney request. The Firm uses Maix and approved technology providers under Firm direction to help receive, organize, summarize, and prepare that information for legal review.
          </p>
          <p>
            Lomax Law Firm treats Maix Chat communications as confidential communications made for the purpose of seeking legal services and intends to preserve all applicable confidentiality and privilege protections. Whether attorney-client privilege applies to a particular communication depends on its purpose, content, handling, and applicable law; privilege cannot be guaranteed.
          </p>
          <p>
            Using Maix Chat or preparing an attorney request does not by itself establish representation for a specific legal Matter. Specific-Matter representation begins only after the responsible law firm reviews the request, confirms acceptance in writing, and the user completes any required engagement agreement.
          </p>
          <p>
            Maix does not monitor or protect statutes of limitation or other legal deadlines. Users with urgent or time-sensitive matters should promptly contact a licensed attorney.
          </p>
        </section>

        <nav class="public-legal-links" aria-label="Legal documents">
          <router-link to="/app-contract">Limited Scope Agreement</router-link>
          <span aria-hidden="true">|</span>
          <router-link to="/privacy">Privacy Policy</router-link>
          <span aria-hidden="true">|</span>
          <router-link to="/terms">App Terms &amp; Conditions</router-link>
          <span aria-hidden="true">|</span>
          <router-link to="/ai-confidentiality">AI &amp; Confidentiality</router-link>
        </nav>
      </div>
    </footer>
  </div>
</template>

<script>
import { computed, h, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { fetchPublishedLegalDocument } from "@/firebase/firestore";
import maixWordmark from "@/assets/maix-wordmark.png";

const legalPages = [
  { path: "/app-contract", label: "Limited Scope Agreement" },
  { path: "/privacy", label: "Privacy Policy" },
  { path: "/terms", label: "App Terms & Conditions" },
  { path: "/ai-confidentiality", label: "AI & Confidentiality" }
];

const InlineMarkdown = {
  name: "InlineMarkdown",

  props: {
    text: {
      type: String,
      default: ""
    }
  },

  setup(props) {
    return () => h(
      "span",
      parseInlineMarkdown(props.text).map((segment, index) => {
        const key = `${segment.type}-${index}`;

        if (segment.type === "strong") {
          return h("strong", { key }, segment.text);
        }

        if (segment.type === "emphasis") {
          return h("em", { key }, segment.text);
        }

        if (segment.type === "link") {
          return h("a", {
            key,
            href: segment.url,
            target: "_blank",
            rel: "noopener noreferrer"
          }, segment.text);
        }

        return segment.text;
      })
    );
  }
};

export default {
  name: "LegalDocumentView",

  components: {
    InlineMarkdown
  },

  props: {
    documentId: {
      type: String,
      required: true
    },
    pageTitle: {
      type: String,
      required: true
    }
  },

  setup(props) {
    const route = useRoute();
    const router = useRouter();
    const legalDocument = ref(null);
    const loading = ref(true);
    const errorMessage = ref("");
    let requestSequence = 0;

    const currentPath = computed(() => route.path);

    const formattedBlocks = computed(() => {
      let content = legalDocument.value?.content ?? "";

      if (props.documentId === "example-app-contract") {
        content = content
          .replaceAll("{{CLIENT_NAME}}", "Example App Client")
          .replaceAll("{{SIGNED_DATE}}", "Date electronically signed by Client");
      }

      return parseMarkdownBlocks(content);
    });

    const loadDocument = async () => {
      const requestId = ++requestSequence;
      loading.value = true;
      errorMessage.value = "";
      legalDocument.value = null;
      document.title = `${props.pageTitle} | Maix`;

      try {
        const publishedDocument = await fetchPublishedLegalDocument(props.documentId);

        if (requestId !== requestSequence) { return; }

        if (!publishedDocument?.content) {
          errorMessage.value = "This legal document has not been published yet.";
          return;
        }

        legalDocument.value = publishedDocument;
        document.title = `${publishedDocument.title || props.pageTitle} | Maix`;
      } catch (error) {
        if (requestId !== requestSequence) { return; }
        console.error("Unable to load the published legal document:", error);
        errorMessage.value = "We could not load this document. Please try again.";
      } finally {
        if (requestId === requestSequence) {
          loading.value = false;
        }
      }
    };

    const openLegalPage = event => {
      const path = String(event.target.value ?? "");
      if (path && path !== route.path) { router.push(path); }
    };

    watch(() => props.documentId, () => {
      window.scrollTo(0, 0);
      loadDocument();
    }, { immediate: true });

    return {
      maixWordmark,
      legalPages,
      currentPath,
      formattedBlocks,
      loading,
      errorMessage,
      loadDocument,
      openLegalPage
    };
  }
};

function parseMarkdownBlocks(markdown) {
  const lines = String(markdown).split(/\r?\n/);
  const blocks = [];
  let paragraph = [];
  let listItems = [];
  let listType = null;

  const flushParagraph = () => {
    if (!paragraph.length) { return; }
    blocks.push({ type: "paragraph", text: paragraph.join(" ") });
    paragraph = [];
  };

  const flushList = () => {
    if (!listItems.length) { return; }
    blocks.push({
      type: listType === "ordered" ? "ordered-list" : "list",
      items: listItems
    });
    listItems = [];
    listType = null;
  };

  lines.forEach(line => {
    const trimmed = line.trim();
    const heading = trimmed.match(/^(#{1,3})\s+(.+)$/);
    const unorderedListItem = trimmed.match(/^[-*]\s+(.+)$/);
    const orderedListItem = trimmed.match(/^\d+[.]\s+(.+)$/);

    if (heading) {
      flushParagraph();
      flushList();
      blocks.push({ type: `heading-${heading[1].length}`, text: heading[2] });
      return;
    }

    if (unorderedListItem || orderedListItem) {
      flushParagraph();
      const nextListType = orderedListItem ? "ordered" : "unordered";

      if (listType && listType !== nextListType) {
        flushList();
      }

      listType = nextListType;
      listItems.push((orderedListItem ?? unorderedListItem)[1]);
      return;
    }

    if (!trimmed) {
      flushParagraph();
      flushList();
      return;
    }

    flushList();
    paragraph.push(trimmed);
  });

  flushParagraph();
  flushList();
  return blocks;
}

function parseInlineMarkdown(value) {
  const source = String(value);
  const tokenPattern = /(\[[^\]]+\]\(https?:\/\/[^\s)]+\)|\*\*[^*]+\*\*|\*[^*]+\*)/g;
  const segments = [];
  let cursor = 0;

  for (const match of source.matchAll(tokenPattern)) {
    if (match.index > cursor) {
      segments.push({ type: "text", text: source.slice(cursor, match.index) });
    }

    const token = match[0];
    const link = token.match(/^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/);

    if (link) {
      segments.push({ type: "link", text: link[1], url: link[2] });
    } else if (token.startsWith("**")) {
      segments.push({ type: "strong", text: token.slice(2, -2) });
    } else {
      segments.push({ type: "emphasis", text: token.slice(1, -1) });
    }

    cursor = match.index + token.length;
  }

  if (cursor < source.length) {
    segments.push({ type: "text", text: source.slice(cursor) });
  }

  return segments;
}
</script>

<style scoped>
.public-legal-page {
  min-height: 100%;
  background: #ffffff;
  color: #0a304f;
}

.public-legal-header {
  box-sizing: border-box;
  display: flex;
  width: min(100%, 1780px);
  margin: 0 auto;
  padding: 54px 64px 0;
  align-items: center;
  justify-content: space-between;
  gap: 28px;
}

.public-legal-header a {
  display: inline-block;
}

.public-legal-header img {
  display: block;
  width: 134px;
  height: auto;
}

.legal-page-selector select {
  min-width: 250px;
  padding: 11px 42px 11px 16px;
  border: 1px solid #b7c6d0;
  border-radius: 8px;
  background-color: #ffffff;
  color: #082f4f;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.public-legal-main {
  box-sizing: border-box;
  width: min(100%, 1220px);
  min-height: 590px;
  margin: 0 auto;
  padding: 150px 42px 140px;
}

.public-legal-document {
  color: #123654;
  font-size: 1.2rem;
  line-height: 1.85;
}

.public-legal-document h1,
.public-legal-document h2,
.public-legal-document h3 {
  color: #082f4f;
  font-family: "Dosis", sans-serif;
  font-weight: 700;
  line-height: 1.25;
}

.public-legal-document h1 {
  margin: 0 0 48px;
  font-size: clamp(2rem, 3vw, 2.55rem);
  text-transform: uppercase;
}

.public-legal-document h2 {
  margin: 62px 0 24px;
  font-size: clamp(1.35rem, 2vw, 1.75rem);
  text-transform: uppercase;
}

.public-legal-document h3 {
  margin: 42px 0 18px;
  font-size: 1.25rem;
}

.public-legal-document p,
.public-legal-document ul,
.public-legal-document ol {
  margin: 0 0 26px;
}

.public-legal-document ul,
.public-legal-document ol {
  padding-left: 2.1rem;
}

.public-legal-document li {
  margin-bottom: 12px;
  padding-left: 0.35rem;
}

.public-legal-document a {
  color: #061b2c;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 4px;
}

.public-legal-status {
  display: flex;
  min-height: 280px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #567084;
  text-align: center;
}

.public-legal-status h1 {
  color: #082f4f;
  font-weight: 700;
}

.public-legal-status button {
  margin-top: 8px;
  padding: 12px 24px;
  border: 0;
  border-radius: 8px;
  background: #f7b900;
  color: #082f4f;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.public-legal-spinner {
  width: 34px;
  height: 34px;
  border: 3px solid #dce5ea;
  border-top-color: #148bb8;
  border-radius: 50%;
  animation: public-legal-spin 0.8s linear infinite;
}

@keyframes public-legal-spin {
  to { transform: rotate(360deg); }
}

.public-legal-footer {
  background: #082f4f;
  color: #ffffff;
}

.public-legal-footer-content {
  box-sizing: border-box;
  width: min(100%, 1120px);
  margin: 0 auto;
  padding: 88px 42px 76px;
  text-align: center;
}

.footer-eyebrow {
  margin: 0 0 20px;
  font-size: 1.35rem;
  font-weight: 700;
  text-transform: uppercase;
}

.public-legal-footer h2 {
  margin: 0;
  color: #ffffff;
  font-size: clamp(1.8rem, 3vw, 2.35rem);
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.app-store-button {
  display: inline-flex;
  min-width: 216px;
  margin-top: 34px;
  padding: 9px 20px;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border-radius: 12px;
  background: #168ab5;
  color: #ffffff;
  line-height: 1;
  text-decoration: none;
}

.apple-mark {
  font-size: 3rem;
}

.app-store-button span:last-child {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.app-store-button small {
  font-size: 0.86rem;
}

.app-store-button strong {
  margin-top: 4px;
  font-size: 1.72rem;
  font-weight: 400;
}

.built-by-button {
  display: block;
  max-width: 500px;
  margin: 50px auto 60px;
  padding: 18px 22px;
  border-radius: 10px;
  background: #f7b900;
  color: #082f4f;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-decoration: none;
  text-transform: uppercase;
}

.app-store-button:hover,
.built-by-button:hover {
  transform: translateY(-2px);
  filter: brightness(1.04);
}

.footer-separator {
  height: 1px;
  background: rgba(255, 255, 255, 0.75);
}

.maix-chat-notice {
  padding: 72px 4px 58px;
  color: #ffffff;
  text-align: left;
}

.maix-chat-notice h3 {
  margin: 0 0 30px;
  color: #ffffff;
  font-size: 1.25rem;
  font-weight: 700;
  text-transform: uppercase;
}

.maix-chat-notice p {
  margin: 0 0 25px;
  font-size: 1.05rem;
  line-height: 1.85;
}

.maix-chat-notice p:last-child {
  margin-bottom: 0;
}

.public-legal-links {
  display: flex;
  padding-top: 30px;
  align-items: center;
  justify-content: center;
  gap: 19px;
  flex-wrap: wrap;
}

.public-legal-links a {
  color: #f7b900;
  font-size: 1.05rem;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 5px;
}

.public-legal-links a.router-link-active {
  color: #ffffff;
  text-decoration-color: #f7b900;
}

@media (max-width: 760px) {
  .public-legal-header {
    padding: 34px 24px 0;
    align-items: flex-start;
  }

  .public-legal-header img {
    width: 102px;
  }

  .legal-page-selector select {
    min-width: 0;
    max-width: 190px;
    padding: 9px 32px 9px 12px;
    font-size: 0.86rem;
  }

  .public-legal-main {
    min-height: 420px;
    padding: 92px 25px 90px;
  }

  .public-legal-document {
    font-size: 1.04rem;
    line-height: 1.7;
  }

  .public-legal-document h1 {
    margin-bottom: 34px;
  }

  .public-legal-document h2 {
    margin-top: 48px;
  }

  .public-legal-footer-content {
    padding: 68px 28px 60px;
  }

  .app-store-button {
    margin-top: 34px;
  }

  .built-by-button {
    margin: 44px auto 52px;
  }

  .maix-chat-notice {
    padding: 54px 0 42px;
  }

  .maix-chat-notice h3 {
    font-size: 1.08rem;
  }

  .maix-chat-notice p {
    font-size: 0.98rem;
    line-height: 1.72;
  }

  .public-legal-links {
    padding-top: 24px;
    flex-direction: column;
    gap: 18px;
  }

  .public-legal-links span {
    display: none;
  }
}
</style>
